<template lang="pug">
div
  v-radio-group.mt-0(v-model='mode' row :disabled='busy || seedOnly' @change='emitStatus')
    v-radio(:label=`$t('TENANT_PIPELINE.MODE_EXISTING')` value='existing' color='#18bedf')
    v-radio(:label=`$t('TENANT_PIPELINE.MODE_NEW')` value='new' color='#18bedf')
  div(v-if='mode === "existing"')
    .mb-1(style='color:#444444; font-weight: 500; font-size: 14px;') {{$t('ADD_SUPERUSER.TENANTNAME')}}
    v-autocomplete(
      v-model='selectedSchema'
      :items='tenantListItems'
      item-text='schema_name'
      item-value='schema_name'
      :placeholder=`$t('ADD_SUPERUSER.SELECT_TENANT')`
      outlined
      dense
      :disabled='busy'
      @change='emitStatus'
    )
      template(v-slot:item='{ item }')
        span {{ item.schema_name }}
        span.ml-2(style='color:#888888;') {{ item.description }}
  v-form(v-else v-model='valid' ref='form')
    v-alert.mb-3(v-if='seedOnly' type='warning' dense outlined) {{$t('TENANT_PIPELINE.SEED_RETRY_HINT')}}
    .mb-1(style='color:#444444; font-weight: 500; font-size: 14px;') {{$t('ADD_TENANT.TENANTNAME')}}
    v-text-field.mb-2(
      v-model='form.name'
      outlined
      dense
      :rules='formRule.nameRules'
      :hint=`$t('ADD_TENANT.NAME_HINT')`
      persistent-hint
      :disabled='busy || seedOnly'
      @input='emitStatus'
    )
    .mb-1(style='color:#444444; font-weight: 500; font-size: 14px;') {{$t('ADD_TENANT.LICENSED')}}
    v-text-field.mb-2(
      v-model='form.licensed'
      type='number'
      :min='1'
      outlined
      dense
      :rules='formRule.integerRules'
      :hint=`$t('ADD_TENANT.LICENSED_HINT')`
      persistent-hint
      :disabled='busy || seedOnly'
      @input='emitStatus'
    )
    .mb-1(style='color:#444444; font-weight: 500; font-size: 14px;') {{$t('ADD_TENANT.DESCRIPTION')}}
    v-textarea(v-model='form.description' outlined height='80' no-resize :disabled='busy || seedOnly')
</template>

<script>
import Vue from "vue";
import i18n from '/common/plugins/vue-i18n.js';
import TenantCenterService from "@/services/TenantCenterService";
import { errorText } from "@/components/TenantCenter/Pipeline/pipelineUtils";

export default Vue.extend({
  props: {
    tenantListItems: { type: Array, require: true },
    busy: { type: Boolean, default: false },
  },
  data() {
    return {
      mode: 'existing',
      selectedSchema: null,
      valid: false,
      form: { name: '', licensed: 1, description: '' },
      // 租戶已建立、但載入基本資料失敗：只重試載入，不再建立租戶
      seedOnly: false,
      formRule: {
        nameRules: [
          (v) => !!v || i18n.t('RULE.RULE_R'),
          (v) => /^.{0,63}$/.test(v) || i18n.t('RULE.RULE_100'),
          (v) => /^[a-z0-9_]+$/.test(v) || i18n.t('RULE.RULE_LOWER_ALPHA_NUMERIC'),
        ],
        integerRules: [
          (v) => !!v || i18n.t('RULE.RULE_R'),
          (v) => /^[1-9]\d*$/.test(v) || i18n.t('RULE.RULE_ALPHA_NUMERIC'),
        ],
      },
    };
  },
  mounted() {
    this.emitStatus();
  },
  methods: {
    emitStatus() {
      this.$nextTick(() => {
        if (this.mode === 'existing') {
          this.$emit('status', { dirty: false, ready: !!this.selectedSchema });
        } else {
          const nameOk = /^[a-z0-9_]{1,63}$/.test(this.form.name) && /^[1-9]\d*$/.test(String(this.form.licensed));
          this.$emit('status', { dirty: true, ready: this.seedOnly || nameOk });
        }
      });
    },
    selectedTenant() {
      return this.tenantListItems.find((t) => t.schema_name === this.selectedSchema);
    },
    async submit() {
      const name = this.form.name;
      if (!this.seedOnly) {
        if (!this.$refs.form.validate()) {
          throw new Error(i18n.t('TENANT_PIPELINE.FORM_INVALID'));
        }
        if (this.tenantListItems.some((t) => t.schema_name === name)) {
          throw new Error(i18n.t('TENANT_PIPELINE.TENANT_EXISTS'));
        }
        // 租戶名稱同時作為 schema 名稱：loadSeedData 以 name 當 schema 使用
        const data = {
          name: name,
          schema_name: name,
          domain: name,
          is_primary: true,
          licensed: Number(this.form.licensed),
          description: this.form.description.trim() || name,
        };
        this.$emit('progress', i18n.t('TENANT_PIPELINE.CREATING_TENANT'));
        try {
          await TenantCenterService.pipelineCreateTenant(data);
        } catch (error) {
          throw new Error(errorText(error));
        }
        this.seedOnly = true;
      }
      this.$emit('progress', i18n.t('TENANT_PIPELINE.LOADING_SEED'));
      try {
        await TenantCenterService.pipelineLoadSeedData({ name: name });
      } catch (error) {
        this.emitStatus();
        throw new Error(`${i18n.t('TENANT_PIPELINE.SEED_FAILED')}\n${errorText(error)}`);
      }
      return { schema_name: name, name: name, description: this.form.description.trim() || name };
    },
  },
});
</script>
